/**
 * PositionOpportunitiesTable Component
 *
 * Displays position opportunities grouped by base asset.
 * Each row shows: Symbol | Futures Price | Spot Prices (stacked) | Funding Rate
 * Matches the structure of FuturesSpotTable.
 *
 * PERFORMANCE OPTIMIZATIONS:
 * - React.memo for row components to prevent unnecessary re-renders
 * - useMemo for expensive calculations
 * - Stable references for callbacks
 */

import React, { useState, useMemo, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Avatar,
  Tooltip,
  Stack,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Slider,
  Autocomplete,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BlockIcon from '@mui/icons-material/Block';
import { FundingCountdown } from '../FuturesSpotTable/FundingCountdown';
import { useNewPositionData } from '../../contexts/NewPositionDataContext';
import type { PositionOpportunity, SpotExchangeData } from '../../contexts/NewPositionDataContext';
import { getExchangeLogo } from '../../utils/exchangeUrls';
import { Exchange } from '../../types/futuresSpot';
import { traderTheme } from './traderDashboardTheme';
import { selectSettings, updateSettings } from '../../features/settings/settingsSlice';
import { OpportunityExchange, type OpportunityFilters } from '../../types/settings';
import { useMarketWarnings } from '../../contexts/MarketWarningsContext';
import { MarketWarningBadge } from '../MarketWarningBadge/MarketWarningBadge';

/**
 * Props for PositionOpportunitiesTable
 */
interface PositionOpportunitiesTableProps {
  /** Callback when launch strategy is clicked */
  onLaunchStrategy: (opportunity: PositionOpportunity, spotIndex: number) => void;
  /** Maximum rows to display (0 = no limit) */
  maxRows?: number;
  /** Enable compact mode */
  compact?: boolean;
  /** Set of base assets that already have active positions */
  activePositionBases?: Set<string>;
}

/**
 * Sort configuration
 */
type SortField = 'symbol' | 'differential' | 'fundingRate';
type SortDirection = 'asc' | 'desc';

function getDefaultSortDirection(field: SortField): SortDirection {
  switch (field) {
    case 'symbol':
    case 'differential':
      return 'asc';
    case 'fundingRate':
      return 'desc';
    default:
      return 'asc';
  }
}

// ============================================================================
// Static helpers (defined outside component to avoid recreation)
// ============================================================================

const EXCHANGE_INDEX_MAP: Record<Exchange, number> = {
  [Exchange.Upbit]: 0,
  [Exchange.Bithumb]: 1,
  [Exchange.Binance]: 3,
  [Exchange.BinanceFutures]: 3,
};

function getExchangeIndex(exchange: Exchange): number {
  return EXCHANGE_INDEX_MAP[exchange] ?? 3;
}

function getFundingRateColor(fundingRate: number | null): string {
  if (fundingRate === null) return 'inherit';
  if (fundingRate > 0.001) return '#4caf50';
  if (fundingRate < -0.001) return '#ff6b35';
  return 'inherit';
}

function formatFundingRate(fundingRate: number | null): string {
  if (fundingRate === null) return '—';
  return `${(fundingRate * 100).toFixed(4)}%`;
}

/**
 * Format price preserving significant decimals from server
 * - For prices >= 1: show up to 4 decimals, removing trailing zeros
 * - For prices < 1: show up to 8 decimals to preserve precision for small coins
 * - For prices < 0.0001: use scientific notation or show more decimals
 */
function formatPrice(price: number | null): string {
  if (price === null) return '—';

  // Determine appropriate decimal places based on price magnitude
  let decimals: number;
  if (price >= 1000) {
    decimals = 2; // Large prices: $1,234.56
  } else if (price >= 1) {
    decimals = 4; // Normal prices: $12.3456
  } else if (price >= 0.0001) {
    decimals = 6; // Small prices: $0.001234
  } else {
    decimals = 8; // Very small prices: $0.00001234
  }

  // Format and remove unnecessary trailing zeros
  const formatted = price.toFixed(decimals);
  // Remove trailing zeros after decimal point, but keep at least 2 decimals for readability
  const trimmed = formatted.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');

  // Ensure at least 2 decimal places for prices >= 1
  if (price >= 1 && !trimmed.includes('.')) {
    return `$${trimmed}.00`;
  }
  if (price >= 1) {
    const parts = trimmed.split('.');
    if (parts[1] && parts[1].length < 2) {
      return `$${parts[0]}.${parts[1].padEnd(2, '0')}`;
    }
  }

  return `$${trimmed}`;
}

function convertPriceToUSDT(
  price: number,
  quote: string,
  usdtKrwRate: number | null,
  btcUsdtRate: number | null
): number | null {
  if (quote === 'USDT') return price;
  if (quote === 'KRW' && usdtKrwRate && usdtKrwRate > 0) {
    return price / usdtKrwRate;
  }
  if (quote === 'BTC' && btcUsdtRate && btcUsdtRate > 0) {
    return price * btcUsdtRate;
  }
  return null;
}

// ============================================================================
// Memoized Spot Row Component
// ============================================================================

interface SpotRowProps {
  spotData: SpotExchangeData;
  opportunity: PositionOpportunity;
  spotIndex: number;
  onLaunch: () => void;
  usdtKrwRate: number | null;
  btcUsdtRate: number | null;
  isDisabled?: boolean;
}

/**
 * Custom comparison for SpotRow to detect price changes
 * React.memo's default shallow comparison may miss nested value changes
 */
function areSpotRowPropsEqual(prevProps: SpotRowProps, nextProps: SpotRowProps): boolean {
  // Compare primitive props first (fast)
  if (prevProps.isDisabled !== nextProps.isDisabled) return false;
  if (prevProps.usdtKrwRate !== nextProps.usdtKrwRate) return false;
  if (prevProps.btcUsdtRate !== nextProps.btcUsdtRate) return false;

  // Compare spot data values (the important price data)
  const prevSpot = prevProps.spotData;
  const nextSpot = nextProps.spotData;
  if (prevSpot.exchange !== nextSpot.exchange) return false;
  if (prevSpot.quote !== nextSpot.quote) return false;
  if (prevSpot.differentialPercent !== nextSpot.differentialPercent) return false;
  if (prevSpot.bestAsk?.price !== nextSpot.bestAsk?.price) return false;
  if (prevSpot.lastPrice !== nextSpot.lastPrice) return false;

  // onLaunch is a callback - we assume it's stable via useCallback
  return true;
}

const SpotRow = memo<SpotRowProps>(({ spotData, onLaunch, usdtKrwRate, btcUsdtRate, isDisabled }) => {
  const { t } = useTranslation();
  const exchangeIndex = getExchangeIndex(spotData.exchange);
  const spotPriceUSDT = spotData.bestAsk?.price
    ? convertPriceToUSDT(spotData.bestAsk.price, spotData.quote, usdtKrwRate, btcUsdtRate)
    : null;
  const percentDiff = spotData.differentialPercent;
  const diffColor = percentDiff !== null && percentDiff < 0 ? '#4caf50' : '#ff6b9d';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: isDisabled ? 0.4 : 1 }}>
      <Tooltip title={isDisabled ? t('traderDashboard.opportunities.positionExists') : t('traderDashboard.opportunities.launchStrategy')} arrow>
        <span>
          <IconButton
            onClick={onLaunch}
            disabled={isDisabled || spotData.differentialPercent === null}
            sx={{
              color: isDisabled ? traderTheme.text.muted : traderTheme.accent,
              '&:hover': { backgroundColor: isDisabled ? 'transparent' : `${traderTheme.accent}20` },
              width: 28,
              height: 28,
              padding: 0.5,
            }}
            size="small"
          >
            <RocketLaunchIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>

      <Avatar
        src={getExchangeLogo(exchangeIndex)}
        sx={{ width: 16, height: 16, flexShrink: 0 }}
      />

      <Typography variant="body2" sx={{ minWidth: '80px' }}>
        {spotPriceUSDT !== null ? formatPrice(spotPriceUSDT) : '—'}
      </Typography>

      {percentDiff !== null && (
        <Typography
          variant="body2"
          sx={{ color: isDisabled ? traderTheme.text.muted : diffColor, fontWeight: 'bold', minWidth: '60px', pl: 1 }}
        >
          ({percentDiff >= 0 ? '+' : ''}{percentDiff.toFixed(2)}%)
        </Typography>
      )}

      {spotData.quote !== 'USDT' && spotData.bestAsk?.price && (
        <Typography
          variant="caption"
          sx={{ color: traderTheme.text.primary, fontSize: '0.75rem', pl: 0.5, opacity: isDisabled ? 0.4 : 0.7 }}
        >
          [{spotData.quote === 'KRW' ? '₩' : '₿'}
          {spotData.bestAsk.price.toLocaleString('en-US', {
            minimumFractionDigits: spotData.quote === 'KRW' ? 0 : 0,
            maximumFractionDigits: spotData.quote === 'KRW' ? 2 : 8,
          })}]
        </Typography>
      )}
    </Box>
  );
}, areSpotRowPropsEqual);

SpotRow.displayName = 'SpotRow';

// ============================================================================
// Memoized Funding Rate Icon
// ============================================================================

const FundingRateIcon = memo<{ fundingRate: number | null }>(({ fundingRate }) => {
  if (fundingRate === null) return null;
  if (fundingRate > 0.001) return <ArrowUpward fontSize="small" sx={{ color: '#4caf50' }} />;
  if (fundingRate < -0.001) return <ArrowDownward fontSize="small" sx={{ color: '#ff6b35' }} />;
  return null;
});

FundingRateIcon.displayName = 'FundingRateIcon';

// ============================================================================
// Memoized Opportunity Row Component
// ============================================================================

interface OpportunityRowProps {
  opportunity: PositionOpportunity;
  onLaunchStrategy: (opportunity: PositionOpportunity, spotIndex: number) => void;
  compact: boolean;
  usdtKrwRate: number | null;
  btcUsdtRate: number | null;
  isDisabled?: boolean;
  /** Called when mouse enters the row - freezes sort order */
  onHoverStart?: (baseAsset: string) => void;
  /** Called when mouse leaves the row */
  onHoverEnd?: () => void;
  /** Whether this row is currently hovered */
  isHovered?: boolean;
}

/**
 * Custom comparison for OpportunityRow to detect price and data changes
 * This ensures the row re-renders when prices update from polling
 */
function areOpportunityRowPropsEqual(prevProps: OpportunityRowProps, nextProps: OpportunityRowProps): boolean {
  // Compare primitive props first (fast)
  if (prevProps.compact !== nextProps.compact) return false;
  if (prevProps.isDisabled !== nextProps.isDisabled) return false;
  if (prevProps.isHovered !== nextProps.isHovered) return false;
  if (prevProps.usdtKrwRate !== nextProps.usdtKrwRate) return false;
  if (prevProps.btcUsdtRate !== nextProps.btcUsdtRate) return false;

  // Compare opportunity data
  const prevOpp = prevProps.opportunity;
  const nextOpp = nextProps.opportunity;
  if (prevOpp.baseAsset !== nextOpp.baseAsset) return false;
  if (prevOpp.futuresBestBid?.price !== nextOpp.futuresBestBid?.price) return false;
  if (prevOpp.fundingRate !== nextOpp.fundingRate) return false;
  if (prevOpp.nextFundingTime !== nextOpp.nextFundingTime) return false;
  if (prevOpp.fundingIntervalHours !== nextOpp.fundingIntervalHours) return false;
  if (prevOpp.bestDifferentialPercent !== nextOpp.bestDifferentialPercent) return false;
  if (prevOpp.timestamp !== nextOpp.timestamp) return false;

  // Compare spotData array - if lengths differ or any spot data changed
  if (prevOpp.spotData.length !== nextOpp.spotData.length) return false;
  for (let i = 0; i < prevOpp.spotData.length; i++) {
    const prevSpot = prevOpp.spotData[i];
    const nextSpot = nextOpp.spotData[i];
    if (prevSpot.exchange !== nextSpot.exchange) return false;
    if (prevSpot.quote !== nextSpot.quote) return false;
    if (prevSpot.differentialPercent !== nextSpot.differentialPercent) return false;
    if (prevSpot.bestAsk?.price !== nextSpot.bestAsk?.price) return false;
    if (prevSpot.lastPrice !== nextSpot.lastPrice) return false;
  }

  return true;
}

const OpportunityRow = memo<OpportunityRowProps>(({
  opportunity,
  onLaunchStrategy,
  compact,
  usdtKrwRate,
  btcUsdtRate,
  isDisabled,
  onHoverStart,
  onHoverEnd,
  isHovered,
}) => {
  const { t } = useTranslation();
  const { getWarnedVendors } = useMarketWarnings();

  // Handle mouse enter - freeze sort order
  const handleMouseEnter = useCallback(() => {
    onHoverStart?.(opportunity.baseAsset);
  }, [onHoverStart, opportunity.baseAsset]);

  // Handle mouse leave - restore sort order
  const handleMouseLeave = useCallback(() => {
    onHoverEnd?.();
  }, [onHoverEnd]);

  // Sort spot data once, memoized - most negative first
  const sortedSpotData = useMemo(() => {
    return [...opportunity.spotData].sort((a, b) => {
      const aVal = a.differentialPercent ?? Infinity;
      const bVal = b.differentialPercent ?? Infinity;
      return aVal - bVal;
    });
  }, [opportunity.spotData]);
  const warningVendors = getWarnedVendors(
    opportunity.spotData.map((spot) => ({
      exchange: String(spot.exchange),
      quote: spot.quote,
      base: opportunity.baseAsset,
    })),
  );

  // Create stable launch handlers
  const handleLaunch = useCallback(
    (idx: number) => () => onLaunchStrategy(opportunity, idx),
    [opportunity, onLaunchStrategy]
  );

  // Find the best differential spot index for symbol click
  const bestSpotIndex = useMemo(() => {
    if (sortedSpotData.length === 0) return -1;
    const bestSpot = sortedSpotData[0];
    return opportunity.spotData.findIndex(
      (sd) => sd.exchange === bestSpot.exchange && sd.quote === bestSpot.quote
    );
  }, [sortedSpotData, opportunity.spotData]);

  // Handle symbol click - launch with best differential spot
  const handleSymbolClick = useCallback(() => {
    if (!isDisabled && bestSpotIndex >= 0) {
      onLaunchStrategy(opportunity, bestSpotIndex);
    }
  }, [isDisabled, bestSpotIndex, opportunity, onLaunchStrategy]);

  return (
    <TableRow
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        '&:hover': { backgroundColor: isDisabled ? 'transparent' : traderTheme.bg.hover },
        opacity: isDisabled ? 0.5 : 1,
        // Highlight when hovered (position locked)
        backgroundColor: isHovered ? 'rgba(88, 166, 255, 0.08)' : 'transparent',
        borderLeft: isHovered ? `2px solid ${traderTheme.accent}` : '2px solid transparent',
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
      }}
    >
      {/* Symbol - clickable to launch with best differential */}
      <TableCell sx={{ borderBottom: `1px solid ${traderTheme.border}`, py: compact ? 0.5 : 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <MarketWarningBadge vendors={warningVendors} />
          <Tooltip title={isDisabled ? t('traderDashboard.opportunities.positionExists') : t('traderDashboard.opportunities.launchBestDiff')} arrow>
            <Typography
              variant="body2"
              onClick={handleSymbolClick}
              sx={{
                fontWeight: 'bold',
                color: isDisabled ? traderTheme.text.muted : traderTheme.text.primary,
                cursor: isDisabled ? 'default' : 'pointer',
                transition: 'color 0.15s ease',
                '&:hover': isDisabled ? {} : {
                  color: traderTheme.accent,
                },
              }}
            >
              {opportunity.baseAsset}
              {isDisabled && (
                <Typography component="span" sx={{ ml: 1, fontSize: '0.7rem', color: traderTheme.text.muted }}>
                  {t('traderDashboard.opportunities.active')}
                </Typography>
              )}
            </Typography>
          </Tooltip>
        </Box>
      </TableCell>

      {/* Futures Price */}
      <TableCell
        align="right"
        sx={{ borderBottom: `1px solid ${traderTheme.border}`, py: compact ? 0.5 : 1 }}
      >
        <Typography variant="body2" fontWeight="bold" sx={{ color: isDisabled ? traderTheme.text.muted : 'inherit' }}>
          {formatPrice(opportunity.futuresBestBid?.price ?? null)}
        </Typography>
      </TableCell>

      {/* Spot Prices */}
      <TableCell
        align="left"
        sx={{ borderBottom: `1px solid ${traderTheme.border}`, py: compact ? 0.5 : 1 }}
      >
        <Stack spacing={0.5}>
          {sortedSpotData.map((spotData) => {
            // Find the original index in unsorted spotData array
            const originalIdx = opportunity.spotData.findIndex(
              (sd) => sd.exchange === spotData.exchange && sd.quote === spotData.quote
            );
            return (
              <SpotRow
                key={`${spotData.exchange}-${spotData.quote}`}
                spotData={spotData}
                opportunity={opportunity}
                spotIndex={originalIdx}
                onLaunch={handleLaunch(originalIdx)}
                usdtKrwRate={usdtKrwRate}
                btcUsdtRate={btcUsdtRate}
                isDisabled={isDisabled}
              />
            );
          })}
        </Stack>
      </TableCell>

      {/* Funding Rate */}
      <TableCell
        align="right"
        sx={{ borderBottom: `1px solid ${traderTheme.border}`, py: compact ? 0.5 : 1 }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <FundingRateIcon fundingRate={opportunity.fundingRate} />
            <Typography
              variant="body2"
              fontWeight="bold"
              sx={{ color: isDisabled ? traderTheme.text.muted : getFundingRateColor(opportunity.fundingRate) }}
            >
              {formatFundingRate(opportunity.fundingRate)}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            <FundingCountdown
              nextFundingTime={opportunity.nextFundingTime}
              fundingIntervalHours={opportunity.fundingIntervalHours}
            />
            {opportunity.fundingIntervalHours !== null && ` (${opportunity.fundingIntervalHours}h)`}
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );
}, areOpportunityRowPropsEqual);

OpportunityRow.displayName = 'OpportunityRow';

// ============================================================================
// Search Input Component (memoized)
// ============================================================================

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
}

const SearchInput = memo<SearchInputProps>(({ value, onChange }) => {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value),
    [onChange]
  );

  return (
    <TextField
      size="small"
      placeholder="Search symbol..."
      value={value}
      onChange={handleChange}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ color: traderTheme.text.secondary, fontSize: 18 }} />
          </InputAdornment>
        ),
      }}
      sx={{
        width: 200,
        '& .MuiOutlinedInput-root': {
          height: 32,
          backgroundColor: traderTheme.bg.secondary,
          '& fieldset': { borderColor: traderTheme.border },
          '&:hover fieldset': { borderColor: traderTheme.accent },
          '&.Mui-focused fieldset': { borderColor: traderTheme.accent },
        },
        '& .MuiInputBase-input': {
          fontSize: '0.85rem',
          color: traderTheme.text.primary,
          padding: '6px 8px',
        },
      }}
    />
  );
});

SearchInput.displayName = 'SearchInput';

// ============================================================================
// Main Component
// ============================================================================

// Helper to convert between Exchange enum and OpportunityExchange
const exchangeToOpportunityExchange = (exchange: Exchange): OpportunityExchange => {
  switch (exchange) {
    case Exchange.Binance: return OpportunityExchange.Binance;
    case Exchange.Upbit: return OpportunityExchange.Upbit;
    case Exchange.Bithumb: return OpportunityExchange.Bithumb;
    case Exchange.BinanceFutures: return OpportunityExchange.BinanceFutures;
    default: return OpportunityExchange.Binance;
  }
};

const opportunityExchangeToExchange = (opExchange: OpportunityExchange): Exchange => {
  switch (opExchange) {
    case OpportunityExchange.Binance: return Exchange.Binance;
    case OpportunityExchange.Upbit: return Exchange.Upbit;
    case OpportunityExchange.Bithumb: return Exchange.Bithumb;
    case OpportunityExchange.BinanceFutures: return Exchange.BinanceFutures;
    default: return Exchange.Binance;
  }
};

export const PositionOpportunitiesTable: React.FC<PositionOpportunitiesTableProps> = ({
  onLaunchStrategy,
  maxRows = 0,
  compact = false,
  activePositionBases,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const settings = useSelector(selectSettings);
  const opportunityFilters = settings.opportunityFilters;

  const {
    opportunities,
    loading,
    error,
    connectedExchanges,
    usdtKrwRate,
    btcUsdtRate,
  } = useNewPositionData();

  const [sortField, setSortField] = useState<SortField>('differential');
  const [sortDirection, setSortDirection] = useState<SortDirection>(() => getDefaultSortDirection('differential'));
  const [searchQuery, setSearchQuery] = useState('');
  const [showExcludePanel, setShowExcludePanel] = useState(false);

  // Hover lock state - freezes row position while hovering
  const [hoveredBaseAsset, setHoveredBaseAsset] = useState<string | null>(null);
  const [frozenOrder, setFrozenOrder] = useState<string[] | null>(null);

  // Read filter values from Redux (persisted settings)
  const diffFilterEnabled = opportunityFilters.diffFilterEnabled;
  const diffFilterMin = opportunityFilters.diffFilterMin;
  const fundingFilterEnabled = opportunityFilters.fundingFilterEnabled;
  const fundingFilterMin = opportunityFilters.fundingFilterMin;
  const btcQuoteEnabled = opportunityFilters.btcQuoteEnabled;
  const usdtQuoteEnabled = opportunityFilters.usdtQuoteEnabled;
  const showActivePositions = opportunityFilters.showActivePositions;
  const excludedSymbols = opportunityFilters.excludedSymbols;

  // Convert OpportunityExchange filters to Exchange filters for filtering
  const exchangeFilters = useMemo(() => {
    const result: Record<Exchange, boolean> = {
      [Exchange.Binance]: opportunityFilters.exchangeFilters[OpportunityExchange.Binance],
      [Exchange.Upbit]: opportunityFilters.exchangeFilters[OpportunityExchange.Upbit],
      [Exchange.Bithumb]: opportunityFilters.exchangeFilters[OpportunityExchange.Bithumb],
      [Exchange.BinanceFutures]: opportunityFilters.exchangeFilters[OpportunityExchange.BinanceFutures],
    };
    return result;
  }, [opportunityFilters.exchangeFilters]);

  // Helper to update a single filter value and persist to settings
  const updateFilter = useCallback(<K extends keyof OpportunityFilters>(
    key: K,
    value: OpportunityFilters[K]
  ) => {
    dispatch(updateSettings({
      ...settings,
      opportunityFilters: {
        ...opportunityFilters,
        [key]: value,
      },
    }));
  }, [dispatch, settings, opportunityFilters]);

  // Stable search handler
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  // Toggle handlers - now persist to Redux
  const handleDiffFilterToggle = useCallback(() => {
    updateFilter('diffFilterEnabled', !diffFilterEnabled);
  }, [updateFilter, diffFilterEnabled]);

  const handleFundingFilterToggle = useCallback(() => {
    updateFilter('fundingFilterEnabled', !fundingFilterEnabled);
  }, [updateFilter, fundingFilterEnabled]);

  // Exchange filter toggle
  const handleExchangeToggle = useCallback((exchange: Exchange) => {
    const opExchange = exchangeToOpportunityExchange(exchange);
    const newExchangeFilters = {
      ...opportunityFilters.exchangeFilters,
      [opExchange]: !opportunityFilters.exchangeFilters[opExchange],
    };
    updateFilter('exchangeFilters', newExchangeFilters);
  }, [updateFilter, opportunityFilters.exchangeFilters]);

  // BTC quote filter toggle
  const handleBtcQuoteToggle = useCallback(() => {
    updateFilter('btcQuoteEnabled', !btcQuoteEnabled);
  }, [updateFilter, btcQuoteEnabled]);

  // USDT quote filter toggle
  const handleUsdtQuoteToggle = useCallback(() => {
    updateFilter('usdtQuoteEnabled', !usdtQuoteEnabled);
  }, [updateFilter, usdtQuoteEnabled]);

  // Active positions visibility toggle
  const handleActivePositionsToggle = useCallback(() => {
    updateFilter('showActivePositions', !showActivePositions);
  }, [updateFilter, showActivePositions]);

  // Slider handlers
  const handleDiffSliderChange = useCallback((_: Event, value: number | number[]) => {
    updateFilter('diffFilterMin', value as number);
  }, [updateFilter]);

  const handleFundingSliderChange = useCallback((_: Event, value: number | number[]) => {
    updateFilter('fundingFilterMin', value as number);
  }, [updateFilter]);

  // Toggle exclude panel
  const handleToggleExcludePanel = useCallback(() => {
    setShowExcludePanel((prev) => !prev);
  }, []);

  // Handle excluded symbols change
  const handleExcludedSymbolsChange = useCallback((_: any, newValue: string[]) => {
    updateFilter('excludedSymbols', newValue);
  }, [updateFilter]);

  // Hover lock handlers - freeze row position on hover
  const handleRowHoverStart = useCallback((baseAsset: string, currentOrder: string[]) => {
    setHoveredBaseAsset(baseAsset);
    setFrozenOrder(currentOrder);
  }, []);

  const handleRowHoverEnd = useCallback(() => {
    setHoveredBaseAsset(null);
    setFrozenOrder(null);
  }, []);

  // Get available symbols for exclusion autocomplete
  const availableSymbols = useMemo(() => {
    const symbols = new Set<string>();
    opportunities.forEach((opp) => {
      symbols.add(opp.baseAsset);
    });
    return Array.from(symbols).sort();
  }, [opportunities]);

  // Filter and sort opportunities - memoized (natural sort order)
  const naturalSortedOpportunities = useMemo(() => {
    // Filter by search query first
    let filtered = searchQuery
      ? opportunities.filter((opp) =>
          opp.baseAsset.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : opportunities;

    // Apply excluded symbols filter (remove symbols that user has excluded)
    if (excludedSymbols.length > 0) {
      const excludedSet = new Set(excludedSymbols.map((s) => s.toUpperCase()));
      filtered = filtered.filter((opp) => !excludedSet.has(opp.baseAsset.toUpperCase()));
    }

    // Apply exchange filter - filter out opportunities where ALL spot exchanges are disabled
    // Also filter spotData within each opportunity
    // Also filter by quote currency (BTC)
    // IMPORTANT: Recalculate bestDifferentialPercent after filtering spotData
    filtered = filtered
      .map((opp) => {
        const filteredSpotData = opp.spotData.filter((sd) => {
          // Check exchange filter
          if (!exchangeFilters[sd.exchange]) return false;
          // Check BTC quote filter
          if (!btcQuoteEnabled && sd.quote === 'BTC') return false;
          // Check USDT quote filter
          if (!usdtQuoteEnabled && sd.quote === 'USDT') return false;
          return true;
        });

        // Recalculate bestDifferentialPercent from filtered spot data
        let newBestDiff: number | null = null;
        let newBestExchange: Exchange | null = null;
        for (const sd of filteredSpotData) {
          if (sd.differentialPercent !== null) {
            if (newBestDiff === null || sd.differentialPercent < newBestDiff) {
              newBestDiff = sd.differentialPercent;
              newBestExchange = sd.exchange;
            }
          }
        }

        return {
          ...opp,
          spotData: filteredSpotData,
          bestDifferentialPercent: newBestDiff,
          bestDifferentialExchange: newBestExchange,
        };
      })
      .filter((opp) => opp.spotData.length > 0);

    // Apply difference filter AFTER exchange filtering (uses recalculated bestDifferentialPercent)
    if (diffFilterEnabled) {
      filtered = filtered.filter(
        (opp) => opp.bestDifferentialPercent !== null && opp.bestDifferentialPercent <= diffFilterMin
      );
    }

    // Apply funding rate filter
    if (fundingFilterEnabled) {
      filtered = filtered.filter(
        (opp) => opp.fundingRate !== null && opp.fundingRate * 100 >= fundingFilterMin
      );
    }

    // Apply active positions filter - hide positions that are already opened
    if (!showActivePositions && activePositionBases) {
      filtered = filtered.filter((opp) => !activePositionBases.has(opp.baseAsset));
    }

    const sorted = [...filtered].sort((a, b) => {
      switch (sortField) {
        case 'symbol':
          return sortDirection === 'asc'
            ? a.baseAsset.localeCompare(b.baseAsset)
            : b.baseAsset.localeCompare(a.baseAsset);

        case 'differential': {
          const aVal = a.bestDifferentialPercent ?? Infinity;
          const bVal = b.bestDifferentialPercent ?? Infinity;
          return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        }

        case 'fundingRate': {
          const aVal = a.fundingRate ?? 0;
          const bVal = b.fundingRate ?? 0;
          if (aVal === bVal) {
            const diffA = a.bestDifferentialPercent ?? Infinity;
            const diffB = b.bestDifferentialPercent ?? Infinity;
            return diffA - diffB;
          }
          return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        }

        default:
          return 0;
      }
    });

    return maxRows > 0 ? sorted.slice(0, maxRows) : sorted;
  }, [opportunities, sortField, sortDirection, maxRows, searchQuery, diffFilterEnabled, diffFilterMin, fundingFilterEnabled, fundingFilterMin, exchangeFilters, btcQuoteEnabled, usdtQuoteEnabled, showActivePositions, activePositionBases, excludedSymbols]);

  // Extract current natural order (array of baseAssets)
  const currentNaturalOrder = useMemo(() => {
    return naturalSortedOpportunities.map((opp) => opp.baseAsset);
  }, [naturalSortedOpportunities]);

  // Display opportunities - uses frozen order when hovering, otherwise natural order
  // Data always updates, only position is locked
  const sortedOpportunities = useMemo(() => {
    // If not hovering or no frozen order, use natural sort
    if (!frozenOrder || !hoveredBaseAsset) {
      return naturalSortedOpportunities;
    }

    // Create a map for O(1) lookup of latest data by baseAsset
    const dataMap = new Map<string, PositionOpportunity>();
    for (const opp of naturalSortedOpportunities) {
      dataMap.set(opp.baseAsset, opp);
    }

    // Rebuild the list in frozen order, but with latest data
    const result: PositionOpportunity[] = [];
    for (const baseAsset of frozenOrder) {
      const opp = dataMap.get(baseAsset);
      if (opp) {
        result.push(opp);
      }
    }

    // Add any new items that weren't in the frozen order at the end
    for (const opp of naturalSortedOpportunities) {
      if (!frozenOrder.includes(opp.baseAsset)) {
        result.push(opp);
      }
    }

    return result;
  }, [naturalSortedOpportunities, frozenOrder, hoveredBaseAsset]);

  // Stable sort handler
  const handleSortChange = useCallback((field: SortField) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDirection(getDefaultSortDirection(field));
      return field;
    });
    setFrozenOrder(null);
    setHoveredBaseAsset(null);
  }, []);

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color={traderTheme.text.secondary}>{t('traderDashboard.opportunities.loading')}</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color={traderTheme.loss}>Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header with Filters */}
      <Stack spacing={1} sx={{ mb: 1, px: 1, flexShrink: 0 }}>
        {/* Top row: filters and search */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <FilterListIcon sx={{ color: traderTheme.text.secondary, fontSize: 18 }} />

            {/* Difference filter toggle */}
            <Chip
              label={`Diff ≤ ${diffFilterMin.toFixed(1)}%`}
              size="small"
              onClick={handleDiffFilterToggle}
              sx={{
                backgroundColor: diffFilterEnabled
                  ? 'rgba(88, 166, 255, 0.2)'
                  : traderTheme.bg.secondary,
                color: diffFilterEnabled ? traderTheme.accent : traderTheme.text.secondary,
                border: `1px solid ${diffFilterEnabled ? traderTheme.accent : traderTheme.border}`,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: diffFilterEnabled
                    ? 'rgba(88, 166, 255, 0.3)'
                    : 'rgba(88, 166, 255, 0.1)',
                },
              }}
            />

            {/* Funding rate filter toggle */}
            <Chip
              label={`Funding ≥ ${fundingFilterMin.toFixed(2)}%`}
              size="small"
              onClick={handleFundingFilterToggle}
              sx={{
                backgroundColor: fundingFilterEnabled
                  ? 'rgba(63, 185, 80, 0.2)'
                  : traderTheme.bg.secondary,
                color: fundingFilterEnabled ? traderTheme.profit : traderTheme.text.secondary,
                border: `1px solid ${fundingFilterEnabled ? traderTheme.profit : traderTheme.border}`,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: fundingFilterEnabled
                    ? 'rgba(63, 185, 80, 0.3)'
                    : 'rgba(63, 185, 80, 0.1)',
                },
              }}
            />

            {/* Divider */}
            <Box sx={{ width: 1, height: 20, backgroundColor: traderTheme.border, mx: 0.5 }} />

            {/* Exchange filter toggles with logos */}
            <Tooltip title="Binance" arrow>
              <IconButton
                onClick={() => handleExchangeToggle(Exchange.Binance)}
                size="small"
                sx={{
                  p: 0.5,
                  opacity: exchangeFilters[Exchange.Binance] ? 1 : 0.3,
                  border: `1px solid ${exchangeFilters[Exchange.Binance] ? '#f3ba2f' : traderTheme.border}`,
                  '&:hover': { opacity: 1, backgroundColor: 'rgba(243, 186, 47, 0.1)' },
                }}
              >
                <Avatar src={getExchangeLogo(3)} sx={{ width: 18, height: 18 }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Upbit" arrow>
              <IconButton
                onClick={() => handleExchangeToggle(Exchange.Upbit)}
                size="small"
                sx={{
                  p: 0.5,
                  opacity: exchangeFilters[Exchange.Upbit] ? 1 : 0.3,
                  border: `1px solid ${exchangeFilters[Exchange.Upbit] ? '#005aff' : traderTheme.border}`,
                  '&:hover': { opacity: 1, backgroundColor: 'rgba(0, 90, 255, 0.1)' },
                }}
              >
                <Avatar src={getExchangeLogo(0)} sx={{ width: 18, height: 18 }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Bithumb" arrow>
              <IconButton
                onClick={() => handleExchangeToggle(Exchange.Bithumb)}
                size="small"
                sx={{
                  p: 0.5,
                  opacity: exchangeFilters[Exchange.Bithumb] ? 1 : 0.3,
                  border: `1px solid ${exchangeFilters[Exchange.Bithumb] ? '#ff7a00' : traderTheme.border}`,
                  '&:hover': { opacity: 1, backgroundColor: 'rgba(255, 122, 0, 0.1)' },
                }}
              >
                <Avatar src={getExchangeLogo(1)} sx={{ width: 18, height: 18 }} />
              </IconButton>
            </Tooltip>

            {/* Divider */}
            <Box sx={{ width: 1, height: 20, backgroundColor: traderTheme.border, mx: 0.5 }} />

            {/* BTC quote filter */}
            <Tooltip title="BTC Quote" arrow>
              <Chip
                label="₿"
                size="small"
                onClick={handleBtcQuoteToggle}
                sx={{
                  backgroundColor: btcQuoteEnabled
                    ? 'rgba(247, 147, 26, 0.2)'
                    : traderTheme.bg.secondary,
                  color: btcQuoteEnabled ? '#f7931a' : traderTheme.text.muted,
                  border: `1px solid ${btcQuoteEnabled ? '#f7931a' : traderTheme.border}`,
                  cursor: 'pointer',
                  opacity: btcQuoteEnabled ? 1 : 0.5,
                  fontWeight: 'bold',
                  '&:hover': { opacity: 1 },
                }}
              />
            </Tooltip>

            {/* USDT quote filter */}
            <Tooltip title="USDT Quote" arrow>
              <Chip
                label="₮"
                size="small"
                onClick={handleUsdtQuoteToggle}
                sx={{
                  backgroundColor: usdtQuoteEnabled
                    ? 'rgba(80, 175, 149, 0.2)'
                    : traderTheme.bg.secondary,
                  color: usdtQuoteEnabled ? '#50AF95' : traderTheme.text.muted,
                  border: `1px solid ${usdtQuoteEnabled ? '#50AF95' : traderTheme.border}`,
                  cursor: 'pointer',
                  opacity: usdtQuoteEnabled ? 1 : 0.5,
                  fontWeight: 'bold',
                  '&:hover': { opacity: 1 },
                }}
              />
            </Tooltip>

            {/* Divider */}
            <Box sx={{ width: 1, height: 20, backgroundColor: traderTheme.border, mx: 0.5 }} />

            {/* Active positions visibility toggle */}
            <Tooltip title={showActivePositions ? t('traderDashboard.opportunities.showingActive') : t('traderDashboard.opportunities.hidingActive')} arrow>
              <IconButton
                onClick={handleActivePositionsToggle}
                size="small"
                sx={{
                  p: 0.5,
                  color: showActivePositions ? traderTheme.accent : traderTheme.text.muted,
                  border: `1px solid ${showActivePositions ? traderTheme.accent : traderTheme.border}`,
                  opacity: showActivePositions ? 1 : 0.5,
                  '&:hover': {
                    opacity: 1,
                    backgroundColor: showActivePositions
                      ? 'rgba(88, 166, 255, 0.1)'
                      : 'rgba(255, 255, 255, 0.05)',
                  },
                }}
              >
                <VisibilityIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>

            {/* Exclude symbols toggle */}
            <Tooltip title={excludedSymbols.length > 0 ? t('traderDashboard.opportunities.symbolsExcluded', { count: excludedSymbols.length }) : t('traderDashboard.opportunities.excludeSymbols')} arrow>
              <IconButton
                onClick={handleToggleExcludePanel}
                size="small"
                sx={{
                  p: 0.5,
                  color: excludedSymbols.length > 0 || showExcludePanel ? '#ff6b6b' : traderTheme.text.muted,
                  border: `1px solid ${excludedSymbols.length > 0 || showExcludePanel ? '#ff6b6b' : traderTheme.border}`,
                  opacity: excludedSymbols.length > 0 || showExcludePanel ? 1 : 0.5,
                  '&:hover': {
                    opacity: 1,
                    backgroundColor: 'rgba(255, 107, 107, 0.1)',
                  },
                }}
              >
                <BlockIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>

            <Typography variant="caption" sx={{ color: traderTheme.text.muted, ml: 1 }}>
              {sortedOpportunities.length}/{opportunities.length}
            </Typography>
          </Stack>

          <SearchInput value={searchQuery} onChange={handleSearchChange} />
        </Stack>

        {/* Excluded symbols panel */}
        {showExcludePanel && (
          <Box sx={{ px: 1, pb: 1 }}>
            <Autocomplete
              multiple
              size="small"
              options={availableSymbols}
              value={excludedSymbols}
              onChange={handleExcludedSymbolsChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t('traderDashboard.opportunities.selectSymbolsToExclude')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: traderTheme.bg.secondary,
                      '& fieldset': { borderColor: traderTheme.border },
                      '&:hover fieldset': { borderColor: '#ff6b6b' },
                      '&.Mui-focused fieldset': { borderColor: '#ff6b6b' },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.85rem',
                      color: traderTheme.text.primary,
                    },
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    size="small"
                    {...getTagProps({ index })}
                    key={option}
                    sx={{
                      borderColor: '#ff6b6b',
                      color: '#ff6b6b',
                      '& .MuiChip-deleteIcon': { color: '#ff6b6b' },
                    }}
                  />
                ))
              }
              disableCloseOnSelect
              sx={{
                '& .MuiAutocomplete-tag': {
                  margin: '2px',
                },
              }}
            />
          </Box>
        )}

        {/* Sliders row (only show when filter is enabled) */}
        {(diffFilterEnabled || fundingFilterEnabled) && (
          <Stack direction="row" spacing={3} sx={{ px: 1 }}>
            {/* Always reserve space for diff slider when any filter is enabled */}
            <Box sx={{ width: 150, visibility: diffFilterEnabled ? 'visible' : 'hidden' }}>
              <Typography variant="caption" sx={{ color: traderTheme.text.secondary, fontSize: '0.7rem' }}>
                Max Diff: {diffFilterMin.toFixed(1)}%
              </Typography>
              <Slider
                value={diffFilterMin}
                onChange={handleDiffSliderChange}
                min={-5}
                max={5}
                step={0.1}
                size="small"
                sx={{
                  color: traderTheme.accent,
                  '& .MuiSlider-thumb': { width: 12, height: 12 },
                  '& .MuiSlider-track': { height: 3 },
                  '& .MuiSlider-rail': { height: 3, backgroundColor: traderTheme.border },
                }}
              />
            </Box>
            <Box sx={{ width: 150, visibility: fundingFilterEnabled ? 'visible' : 'hidden' }}>
              <Typography variant="caption" sx={{ color: traderTheme.text.secondary, fontSize: '0.7rem' }}>
                Min Funding: {fundingFilterMin.toFixed(2)}%
              </Typography>
              <Slider
                value={fundingFilterMin}
                onChange={handleFundingSliderChange}
                min={0}
                max={0.1}
                step={0.005}
                size="small"
                sx={{
                  color: traderTheme.profit,
                  '& .MuiSlider-thumb': { width: 12, height: 12 },
                  '& .MuiSlider-track': { height: 3 },
                  '& .MuiSlider-rail': { height: 3, backgroundColor: traderTheme.border },
                }}
              />
            </Box>
          </Stack>
        )}
      </Stack>

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{
          backgroundColor: traderTheme.bg.primary,
          border: `1px solid ${traderTheme.border}`,
          flex: 1,
          overflow: 'auto',
        }}
      >
        <Table stickyHeader size={compact ? 'small' : 'medium'}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  backgroundColor: traderTheme.bg.secondary,
                  borderBottom: `1px solid ${traderTheme.border}`,
                  color: traderTheme.text.secondary,
                  fontWeight: 600,
                }}
              >
                <TableSortLabel
                  active={sortField === 'symbol'}
                  direction={sortField === 'symbol' ? sortDirection : 'asc'}
                  onClick={() => handleSortChange('symbol')}
                  sx={{
                    color: `${traderTheme.text.secondary} !important`,
                    '&:hover': { color: `${traderTheme.text.primary} !important` },
                  }}
                >
                  {t('traderDashboard.opportunities.headers.symbol')}
                </TableSortLabel>
              </TableCell>

              <TableCell
                align="right"
                sx={{
                  backgroundColor: traderTheme.bg.secondary,
                  borderBottom: `1px solid ${traderTheme.border}`,
                  color: traderTheme.text.secondary,
                  fontWeight: 600,
                }}
              >
                {t('traderDashboard.opportunities.headers.futuresPrice')}
              </TableCell>

              <TableCell
                align="left"
                sx={{
                  backgroundColor: traderTheme.bg.secondary,
                  borderBottom: `1px solid ${traderTheme.border}`,
                  color: traderTheme.text.secondary,
                  fontWeight: 600,
                }}
              >
                <TableSortLabel
                  active={sortField === 'differential'}
                  direction={sortField === 'differential' ? sortDirection : 'desc'}
                  onClick={() => handleSortChange('differential')}
                  sx={{
                    color: `${traderTheme.text.secondary} !important`,
                    '&:hover': { color: `${traderTheme.text.primary} !important` },
                  }}
                >
                  {t('traderDashboard.opportunities.headers.spotPrice')}
                </TableSortLabel>
              </TableCell>

              <TableCell
                align="right"
                sx={{
                  backgroundColor: traderTheme.bg.secondary,
                  borderBottom: `1px solid ${traderTheme.border}`,
                  color: traderTheme.text.secondary,
                  fontWeight: 600,
                }}
              >
                <TableSortLabel
                  active={sortField === 'fundingRate'}
                  direction={sortField === 'fundingRate' ? sortDirection : 'desc'}
                  onClick={() => handleSortChange('fundingRate')}
                  sx={{
                    color: `${traderTheme.text.secondary} !important`,
                    '&:hover': { color: `${traderTheme.text.primary} !important` },
                  }}
                >
                  {t('traderDashboard.opportunities.headers.fundingRate')}
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedOpportunities.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  sx={{ textAlign: 'center', py: 4, color: traderTheme.text.muted }}
                >
                  {connectedExchanges === 0
                    ? t('traderDashboard.opportunities.connecting')
                    : t('traderDashboard.opportunities.noOpportunities')}
                </TableCell>
              </TableRow>
            ) : (
              sortedOpportunities.map((opportunity) => (
                <OpportunityRow
                  key={opportunity.baseAsset}
                  opportunity={opportunity}
                  onLaunchStrategy={onLaunchStrategy}
                  compact={compact}
                  usdtKrwRate={usdtKrwRate}
                  btcUsdtRate={btcUsdtRate}
                  isDisabled={activePositionBases?.has(opportunity.baseAsset)}
                  onHoverStart={(baseAsset) => handleRowHoverStart(baseAsset, currentNaturalOrder)}
                  onHoverEnd={handleRowHoverEnd}
                  isHovered={hoveredBaseAsset === opportunity.baseAsset}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PositionOpportunitiesTable;
